var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, makeObservable, observable, toJS, when } from 'mobx';
import nestedProperty from 'nested-property';
import { pushGaData, safeReaction, ErrorUtil } from '../../common';
import { setIsFromSelfServiceCookie } from '../../utils';
import SsoClientFactory from '../SsoClientFactory';
import AuthData from '../util/AuthData';
const getCustomerId = (clientState) => {
    var _a, _b;
    return (_b = (_a = clientState === null || clientState === void 0 ? void 0 : clientState.auth) === null || _a === void 0 ? void 0 : _a.customer) === null || _b === void 0 ? void 0 : _b.customerId;
};
const setDealerStatus = (isDealer) => {
    nestedProperty.set(window, 'telia.megamenu.isDealer', isDealer);
    if (isDealer) {
        pushGaData({ event: 'NONIEvent', eventCategory: 'Status', eventAction: 'Control', eventLabel: 'Dealer' });
    }
    else {
        pushGaData({ event: 'NONIEvent', eventCategory: 'Status', eventAction: 'Control', eventLabel: 'Not Dealer' });
    }
};
const setSsoLoadedStatus = () => {
    nestedProperty.set(window, 'telia.megamenu.isSsoLoaded', true);
    pushGaData({ event: 'NONIEvent', eventCategory: 'SSO', eventAction: 'Control', eventLabel: 'SsoLoaded' });
};
export const isDealerLoggingOutCustomer = (oldClientState, newClientState) => {
    return !!(oldClientState === null || oldClientState === void 0 ? void 0 : oldClientState.auth) && !!newClientState.auth && !!oldClientState.auth.customer && !newClientState.auth.customer;
};
const SSO_INITIALIZATION_TIMEOUT = 5000;
export default class SsoStore {
    constructor(rootStore, props) {
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isInitializationDelayed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "disableSoftAuth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "clientState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "loginOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "logInCallBack", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isLogInCallBackSet", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showDealerSessionExpirationNotice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "client", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "environmentStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "initClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.client) {
                    this.client = new SsoClientFactory(this.rootStore).createClient();
                }
                when(() => this.isInitialized, () => {
                    if (!this.isInitialized) {
                        this.isInitializationDelayed = true;
                        ErrorUtil.pushErrorToTeliaRum(new Error(`Loading sso initial state exceeded timeout: ${SSO_INITIALIZATION_TIMEOUT}ms`));
                    }
                }, { timeout: SSO_INITIALIZATION_TIMEOUT });
            }
        });
        Object.defineProperty(this, "setLoginCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (callback) => {
                this.logInCallBack = callback;
                this.isLogInCallBackSet = true;
            }
        });
        Object.defineProperty(this, "resetLoginCallbackState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.logInCallBack = () => undefined;
                this.isLogInCallBackSet = false;
            }
        });
        Object.defineProperty(this, "setLoginOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (ssoOptions) => {
                const { sectionStore } = this.rootStore;
                if (ssoOptions === null || ssoOptions === void 0 ? void 0 : ssoOptions.forceSelectCustomerId) {
                    ssoOptions.selectMandate = true;
                }
                this.loginOptions = ssoOptions;
                if (ssoOptions && sectionStore.section !== 'sso') {
                    sectionStore.setSection('sso');
                }
            }
        });
        Object.defineProperty(this, "setClientState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (isInitialized, clientState) => {
                this.clientState = clientState;
                this.isInitialized = isInitialized;
            }
        });
        Object.defineProperty(this, "setDisableSoftAuth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (disableSoftAuth) => {
                this.disableSoftAuth = disableSoftAuth;
            }
        });
        Object.defineProperty(this, "getLoginURL", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (options = { selectMandate: true }, ignoreBackendUri = false) => {
                return this.client ? this.client.getLoginUrl(options, ignoreBackendUri) : this.environmentStore.URLData.defaultSsoUrl(options);
            }
        });
        Object.defineProperty(this, "getSessionExpirationLoginUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { skipSsoModule } = this.rootStore.environmentStore;
                return this.client && this.client.getSessionExpiringNoticeUrl(!skipSsoModule);
            }
        });
        Object.defineProperty(this, "getEmployeeSettingsUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { skipSsoModule } = this.rootStore.environmentStore;
                return this.client && this.client.getEmployeeSettingsUrl(!skipSsoModule);
            }
        });
        Object.defineProperty(this, "dispatchLoginStateChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (clientState, prevClientState) => {
                const { sectionStore } = this.rootStore;
                if (clientState) {
                    setSsoLoadedStatus();
                }
                const { app, URLData, isSPA } = this.rootStore.environmentStore;
                if (!prevClientState || !prevClientState.auth) {
                    if (clientState && clientState.auth) {
                        switch (clientState.auth.authLevel) {
                            case 1: {
                                window.postMessage('SoftAuthEvent', window.location.origin);
                                setDealerStatus(false);
                                break;
                            }
                            case 2: {
                                window.postMessage('HardAuthEvent', window.location.origin);
                                setDealerStatus(false);
                                break;
                            }
                            case -1: {
                                window.postMessage('DealerAuthEvent', window.location.origin);
                                setDealerStatus(true);
                                this.listenToSsoDealerSessionExpiration();
                                break;
                            }
                        }
                        if (this.isLogInCallBackSet) {
                            this.logInCallBack();
                        }
                        if (sectionStore.section === 'sso') {
                            sectionStore.setSection(null);
                        }
                    }
                    else if (clientState && clientState.action === 'logout') {
                        setDealerStatus(false);
                        this.logout(isSPA, URLData.publicURL);
                    }
                }
                else {
                    if (!clientState || !clientState.auth) {
                        this.logout(isSPA, URLData.publicURL);
                        setDealerStatus(false);
                    }
                    else if (clientState.auth.customer !== prevClientState.auth.customer) {
                        window.postMessage('MandateChangeEvent', window.location.origin);
                        if (!isSPA && getCustomerId(clientState) !== getCustomerId(prevClientState)) {
                            if (app !== 'web3') {
                                window.location.reload();
                            }
                            return;
                        }
                        else {
                            sectionStore.setSection(null);
                        }
                    }
                    else {
                        sectionStore.setSection(null);
                    }
                }
            }
        });
        Object.defineProperty(this, "listenToSsoDealerSessionExpiration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                window.addEventListener('message', this.showSessionExpirationNoticeEventListener, false);
                window.addEventListener('message', this.closeSessionExpirationNoticeEventListener, false);
            }
        });
        Object.defineProperty(this, "showSessionExpirationNoticeEventListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                var _a;
                if (((_a = event === null || event === void 0 ? void 0 : event.data) === null || _a === void 0 ? void 0 : _a.action) === 'showDealerSessionExpirationNotice') {
                    this.showDealerSessionExpirationNotice = true;
                    this.rootStore.sectionStore.setSection('employeeSettings');
                }
            }
        });
        Object.defineProperty(this, "closeSessionExpirationNoticeEventListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                var _a;
                if (((_a = event === null || event === void 0 ? void 0 : event.data) === null || _a === void 0 ? void 0 : _a.action) === 'dealerSessionExtended') {
                    this.showDealerSessionExpirationNotice = false;
                    this.rootStore.sectionStore.setSection(null);
                }
            }
        });
        Object.defineProperty(this, "logout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (isSPA, publicUrl) => {
                setIsFromSelfServiceCookie(false);
                this.removeDealerEventListeners();
                window.postMessage('LogoutEvent', window.location.origin);
                this.rootStore.profileTypeStore.setProfileTypes([]);
                if (!isSPA) {
                    window.location.href = publicUrl;
                    return;
                }
                else {
                    this.rootStore.sectionStore.setSection(null);
                }
            }
        });
        Object.defineProperty(this, "initSsoOpenFunction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const openSsoWithOptions = (options) => {
                    this.setLoginOptions(options);
                };
                if (!nestedProperty.get(window, 'telia.megamenu.openSsoWithOptions')) {
                    nestedProperty.set(window, 'telia.megamenu.openSsoWithOptions', openSsoWithOptions);
                }
            }
        });
        makeObservable(this);
        this.rootStore = rootStore;
        this.environmentStore = rootStore.environmentStore;
        this.client = props.ssoClient;
        this.setDisableSoftAuth(props.disableSoftAuth);
        const isInitialized = props.ssoClientState && props.ssoClient && Object.keys(props.ssoClientState).length !== 0;
        this.setClientState(isInitialized, toJS(props.ssoClientState));
        this.setLoginOptions(props.loginOptions);
        if (!rootStore.propStateStore.isSsr) {
            this.initClient();
            safeReaction(rootStore, () => this.clientState, (clientState, previousClientState) => this.dispatchLoginStateChange(clientState, previousClientState));
            when(() => this.isInitialized, () => this.initSsoOpenFunction());
        }
    }
    get authData() {
        return new AuthData(this.clientState);
    }
    get isLoggedIn() {
        return this.clientState && !!this.clientState.auth;
    }
    get isDealer() {
        var _a, _b;
        return !!((_b = (_a = this.clientState) === null || _a === void 0 ? void 0 : _a.auth) === null || _b === void 0 ? void 0 : _b.dealer);
    }
    removeDealerEventListeners() {
        if (this.clientState && this.clientState.auth) {
            if (this.clientState.auth.authLevel === -1) {
                window.removeEventListener('message', this.showSessionExpirationNoticeEventListener, false);
                window.removeEventListener('message', this.closeSessionExpirationNoticeEventListener, false);
            }
        }
    }
}
__decorate([
    observable
], SsoStore.prototype, "isInitialized", void 0);
__decorate([
    observable
], SsoStore.prototype, "isInitializationDelayed", void 0);
__decorate([
    observable
], SsoStore.prototype, "disableSoftAuth", void 0);
__decorate([
    observable.struct
], SsoStore.prototype, "clientState", void 0);
__decorate([
    observable
], SsoStore.prototype, "loginOptions", void 0);
__decorate([
    observable
], SsoStore.prototype, "logInCallBack", void 0);
__decorate([
    observable
], SsoStore.prototype, "isLogInCallBackSet", void 0);
__decorate([
    observable
], SsoStore.prototype, "showDealerSessionExpirationNotice", void 0);
__decorate([
    observable
], SsoStore.prototype, "client", void 0);
__decorate([
    computed
], SsoStore.prototype, "authData", null);
__decorate([
    computed
], SsoStore.prototype, "isLoggedIn", null);
__decorate([
    computed
], SsoStore.prototype, "isDealer", null);
__decorate([
    action
], SsoStore.prototype, "initClient", void 0);
__decorate([
    action
], SsoStore.prototype, "setLoginCallback", void 0);
__decorate([
    action
], SsoStore.prototype, "resetLoginCallbackState", void 0);
__decorate([
    action
], SsoStore.prototype, "setLoginOptions", void 0);
__decorate([
    action
], SsoStore.prototype, "setClientState", void 0);
__decorate([
    action
], SsoStore.prototype, "setDisableSoftAuth", void 0);
//# sourceMappingURL=SsoStore.js.map